// client/src/components/news/newsTemplateWithData.js
// Robust HOC: fetch /news?id=<id>, retry on 304, and normalize even when server returns JSON as text/plain.

import React, { Component } from 'react';

export default function newsPagesWithData(WrappedComponent) {
  return class NewsHOC extends Component {
    _isMounted = false;
    _controller = null;

    state = {
      story: null,
      news: [],
      newsItems: [],
      loading: true,
      error: null
    };

    coerceJson(raw) {
      // If server returns JSON but content-type isn't application/json,
      // fetch() may give us a string. DevTools still shows it "as JSON".
      if (typeof raw !== 'string') return raw;

      const trimmed = raw.trim();
      if (!trimmed) return raw;

      // Only attempt parse if it looks like JSON
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          return JSON.parse(trimmed);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('coerceJson: failed to JSON.parse response text');
          return raw;
        }
      }

      return raw;
    }

    async fetchWithRetry(url) {
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const signal = controller ? controller.signal : undefined;

      const fetchOnce = async (u, opts = {}) => {
        const res = await fetch(u, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          signal,
          ...opts
        });

        const ct = res.headers && res.headers.get ? (res.headers.get('content-type') || '') : '';

        // read as json only if server says so; otherwise text
        const readBody = async () => {
          if (ct.includes('application/json')) {
            return res.json().catch(() => null);
          }
          return res.text().catch(() => null);
        };

        return { res, ct, body: await readBody() };
      };

      // First request
      const { res, body } = await fetchOnce(url);

      // Retry path for 304
      if (res.status === 304) {
        const sep = url.includes('?') ? '&' : '?';
        const freshUrl = `${url}${sep}_cb=${Date.now()}`;

        // eslint-disable-next-line no-console
        console.log('fetchWithRetry: got 304, retrying forced:', freshUrl);

        const second = await fetchOnce(freshUrl, { cache: 'no-store' });

        if (!second.res.ok) {
          const msg =
            (typeof second.body === 'string' && second.body) ||
            `HTTP ${second.res.status}`;
          throw new Error(msg);
        }
        return second.body;
      }

      // Normal error handling
      if (!res.ok) {
        const msg = (typeof body === 'string' && body) || `HTTP ${res.status}`;
        throw new Error(msg);
      }

      return body;
    }

    componentDidMount() {
      this._isMounted = true;
      this._controller = typeof AbortController !== 'undefined' ? new AbortController() : null;

      const params = this.props.match?.params || {};
      const candidateId = params.news || params.id || params.articleId || params.newsId || '';

      const id = encodeURIComponent(candidateId);
      const url = '/news?id=' + id;

      // eslint-disable-next-line no-console
      console.log('newsPagesWithData fetching for id param:', candidateId, 'url:', url);

      this._load(url).catch((err) => {
        if (err && err.name === 'AbortError') return;
        if (!this._isMounted) return;

        // eslint-disable-next-line no-console
        console.error('newsPagesWithData fetch error:', err);
        this.setState({
          loading: false,
          error: err?.message || String(err),
          story: null,
          news: [],
          newsItems: []
        });
      });
    }

    async _load(url) {
      let raw = await this.fetchWithRetry(url);

      // NEW: parse if raw is JSON text
      raw = this.coerceJson(raw);

      if (!this._isMounted) return;

      let arr = [];
      if (!raw) {
        arr = [];
      } else if (Array.isArray(raw)) {
        arr = raw;
      } else if (raw.articles && Array.isArray(raw.articles)) {
        arr = raw.articles;
      } else if (raw.newsItems && Array.isArray(raw.newsItems)) {
        arr = raw.newsItems;
      } else if (typeof raw === 'object') {
        arr = [raw];
      } else {
        arr = [];
      }

      const single = arr.length ? arr[0] : null;

      // eslint-disable-next-line no-console
      console.log('newsPagesWithData normalized:', { single, arr });

      this.setState({
        story: single,
        news: arr,
        newsItems: arr,
        loading: false,
        error: null
      });
    }

    componentWillUnmount() {
      this._isMounted = false;
      try { if (this._controller) this._controller.abort(); } catch (e) {}
    }

    render() {
      const pass = {
        ...this.props,
        story: this.state.story,
        news: this.state.news,
        newsItems: this.state.newsItems,
        loading: this.state.loading,
        fetchError: this.state.error
      };
      return <WrappedComponent {...pass} />;
    }
  };
}